import { FETCHABLE_REPORT_KINDS } from '@seller/shared';
import { REPORT_KINDS, type ReportKind } from '../api/client.js';

/**
 * How the Imports page's report chooser is grouped.
 *
 * Kinds the app fetches for itself through the Selling Partner API (the nightly
 * fetcher's own list, `FETCHABLE_REPORT_KINDS` from @seller/shared, so the two
 * cannot drift) stay in the chooser — the manual upload is a fallback for when
 * the API is unavailable — but in their own "Manual (normally fetched
 * automatically)" group, so they are not the everyday choice. The kinds the API
 * cannot fetch (the Business Report by child item and the Restock
 * recommendations) stay as the everyday choices.
 *
 * The grouping is presentation only: every kind keeps its own option value, so
 * a kind in either group uploads exactly as it always has.
 */
export const AUTO_FETCHED_KINDS: ReadonlySet<ReportKind> = new Set<ReportKind>(
  FETCHABLE_REPORT_KINDS,
);

export interface ChooserGroups {
  /** The kinds only a hand download brings in; listed first, ungrouped. */
  everyday: ReportKind[];
  /** The kinds the fetcher brings in; listed last, under the Manual group. */
  manual: ReportKind[];
}

/** The chooser's two groups, each in the canonical report-kind order. */
export function chooserGroups(kinds: readonly ReportKind[] = REPORT_KINDS): ChooserGroups {
  return {
    everyday: kinds.filter((k) => !AUTO_FETCHED_KINDS.has(k)),
    manual: kinds.filter((k) => AUTO_FETCHED_KINDS.has(k)),
  };
}

/**
 * Whether the chooser shows the "the connection card fetches this nightly" note
 * for a kind: only for a fetched kind, and only when the Selling Partner API is
 * configured — otherwise nothing is fetched, and the note would contradict the
 * connection card saying so. Unknown (still loading, or not readable) is no.
 */
export function showFetchedNote(kind: ReportKind, spapiConfigured: boolean | undefined): boolean {
  return spapiConfigured === true && AUTO_FETCHED_KINDS.has(kind);
}
